import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import { FormattedMessage, useIntl } from 'react-intl';
import { PRIVATE_ROUTE_REGISTRY } from 'constants/routes';
import logoSrc from 'assets/images/idecon-header-logo.png';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { toggleMeetingMode } from 'store/features/meeting/slice';
import { selectMeetingMode } from 'store/features/meeting/selector';
import { AppBar, Box, Button, IconButton, Toolbar } from '@mui/material';
import { LanguagePicker, Profile, ColorModeIconDropdown, SnowSettingsButton } from 'components';

type HeaderProps = {
  onMobileMenuToggle?: () => void;
};

const Header = ({ onMobileMenuToggle }: HeaderProps) => {
  // other hooks
  const intl = useIntl();
  const navigate = useNavigate();

  // redux hooks
  const dispatch = useAppDispatch();
  const meetingMode = useAppSelector(selectMeetingMode);

  // sync helper functions
  const onClickLogoHandler = useCallback(() => navigate(PRIVATE_ROUTE_REGISTRY.dashboard.path), [navigate]);

  return (
    <AppBar color="inherit" position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar>
        <Box width="100%" minWidth={0} display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              color="inherit"
              aria-label="open sidebar"
              edge="start"
              onClick={onMobileMenuToggle}
              sx={{ display: { md: 'none' } }}>
              <MenuIcon />
            </IconButton>
            <Box
              component="img"
              sx={{ display: { xs: 'none', sm: 'block' }, width: 100, cursor: 'pointer' }}
              onClick={onClickLogoHandler}
              src={logoSrc}
              alt="logo"
            />
          </Box>
          <Box display="flex" minWidth={0} alignItems="center" gap={{ xs: 0.5, sm: 1, md: 2 }}>
            <Button
              variant="contained"
              size="small"
              color={meetingMode ? 'error' : 'secondary'}
              aria-label={`${intl.formatMessage({ id: 'general.meetingMode' })}: ${meetingMode ? 'ON' : 'OFF'}`}
              onClick={() => dispatch(toggleMeetingMode())}>
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                <FormattedMessage id="general.meetingMode" />:{' '}
              </Box>
              {meetingMode ? 'ON' : 'OFF'}
            </Button>
            <LanguagePicker />
            <ColorModeIconDropdown />
            <SnowSettingsButton />
            <Profile />
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
